import * as Yup from "yup";
import { useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Link,
  Typography,
  Tooltip
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Link as RouterLink } from "react-router-dom";
import axios from "../../../util/axios";
import OTPVerification from "./OTPVerification";
import { useDomainConfig } from "../../../hooks/useDomainConfig";

// ----------------------------------------------------------------------

export default function RegisterForm({ termsUrl, privacyUrl }) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState();
  const [registrationDone, setRegistrationDone] = useState(false);
  const [openOtpVerify, setOpenOtpVerify] = useState(false);
  const [contactType, setContactType] = useState("");
  const [resendOtpPayloadObj, setResendOtpPayloadObj] = useState({});
  const phoneNoRegex = /^\d{10}$/i;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
  const domainConfig = useDomainConfig();
  const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;
  const RegisterSchema = Yup.object().shape({
    name: Yup.string().required("Name required"),
    // email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    contact: Yup.string()
      .test(
        "phone-or-email",
        "Please enter a valid phone number or email address",
        (value) => {
          if (phoneNoRegex.test(value) || emailRegex.test(value)) return true;
          return false;
        }
      )
      .required("Either email or phone no. is required"),
    password: Yup.string()
      .matches(passwordRules, { message: "Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character" })
      .required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      contact: "",
      password: "",
    },
    validationSchema: RegisterSchema,
    onSubmit: (registrationData, { setSubmitting, resetForm }) => {
      setSuccessMessage("");
      setError("");

      (async function () {
        try {
          const registerHelper = async () => {
            let registrationPayload = {
              name: registrationData.name,
              type: emailRegex.test(registrationData.contact)
                ? "email"
                : "phone",
              password: registrationData.password,
              domain: domainConfig.domain,
            };
            if (registrationPayload.type === "email")
              registrationPayload.email = registrationData.contact;
            else {
              registrationPayload.phone = registrationData.contact;
              registrationPayload.countryCode = domainConfig.countryCode;
            }
            const response = await axios(
              `${process.env.REACT_APP_API_END_POINT}auth/v2/register`,
              {
                method: "post",
                data: registrationPayload,
              }
            );
            const registerHelperResp = response.data;
            setContactType(registrationPayload.type);
            setResendOtpPayloadObj(registrationPayload);
            if (registerHelperResp && registerHelperResp.success) {
              if (
                registrationPayload.type === "phone" ||
                registrationPayload.type === "email"
              ) {
                setOpenOtpVerify(true);
                setSubmitting(false);
              } else {
                setSubmitting(false);
                resetForm();
                setSuccessMessage(registerHelperResp.message);
                setRegistrationDone(true);
              }
              // formik.values = initialFormValues;
            } else {
              setSubmitting(false);
              setError(registerHelperResp.message);
            }
          };
          await registerHelper();
        } catch (error) {
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            setSubmitting(false);
            setError(error.response.data.message);
          }
        }
      })();
      //navigate('/dashboard', { replace: true });
    },
  });

  const resendOtpFunc = async (phone) => {
    return new Promise(async (res, rej) => {
      try {
        const response = await axios(
          `${process.env.REACT_APP_API_END_POINT}auth/v2/register-resend-otp`,
          {
            method: "post",
            data: resendOtpPayloadObj,
          }
        );

        const registerHelperResp = response.data;
        if (registerHelperResp && registerHelperResp.success) {
          res("resend success");
        }
      } catch (error) {
        rej(error.response.data.message);
      }
    });
  };

  const submitOtp = (otp) => {
    return new Promise(async (res, rej) => {
      try {
        let otpPayload;
        if(contactType === "email") {
          otpPayload = {
            email: formik.values.contact,
            domain: domainConfig.domain,
            phoneOtp: otp.Otp,
            type: "email",
          }
        } else if(contactType === "phone") {
          otpPayload = {
            domain: domainConfig.domain,
            phone: formik.values.contact,
            countryCode: domainConfig.countryCode,
            phoneOtp: otp.Otp,
            type: "phone",
          }
        }
        const url = `${process.env.REACT_APP_API_END_POINT}auth/v2/register-verify-otp`;
        const response = await axios(url, {
          method: "post",
          data: otpPayload,
        });
        res(response.data);
      } catch (error) {
        rej(error.response.data.message);
      }
    });
  };
  const onSuccessfullVerify = () => {
    resetForm();
    setSuccessMessage("Registration successful");
    setRegistrationDone(true);
  };
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    values,
  } = formik;

  return (
    <FormikProvider value={formik}>
      {error && <div style={{ color: "red", marginBottom: "12px", marginTop: 0, fontSize: "14px" }}>{error}</div>}
      {successMessage && <div style={{ color: "green", marginBottom: "12px" }}>{successMessage}</div>}
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        {!registrationDone && (
          <Stack spacing={3}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Name"
                autoComplete="name"
                {...getFieldProps("name")}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '1rem',
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                  },
                  '& .MuiInputLabel-root': { fontSize: '0.95rem', fontWeight: 500 },
                }}
              />
            </Stack>

            <TextField
              fullWidth
              autoComplete="username"
              type="text"
              label="Email address / Phone number"
              {...getFieldProps("contact")}
              inputProps={{ maxLength: 100 }}
              error={Boolean(touched.contact && errors.contact)}
              helperText={touched.contact && errors.contact}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                },
                '& .MuiInputLabel-root': { fontSize: '0.95rem', fontWeight: 500 },
              }}
            />

            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              label="Password"
              {...getFieldProps("password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPassword ? "Hide password" : "Show password"} placement="top">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((prev) => !prev)}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        {showPassword ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                },
                '& .MuiInputLabel-root': { fontSize: '0.95rem', fontWeight: 500 },
              }}
            />

            {(termsUrl || privacyUrl) && (
              <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mt: 1, mb: 0, fontSize: '0.75rem', lineHeight: 1.5 }}>
                By registering, I agree to <Link component={RouterLink} to="/terms-of-services" underline="always" sx={{ color: 'primary.main' }}>Terms of Service</Link> and <Link component={RouterLink} to="/privacy-policy" underline="always" sx={{ color: 'primary.main' }}>Privacy Policy</Link>.
              </Typography>
            )}

            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              sx={{
                height: 52,
                fontSize: '1.1rem',
                fontWeight: 700,
                letterSpacing: '0.5px',
                textTransform: 'none',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
                mt: 2,
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.5)',
                  transform: 'translateY(-2px)',
                },
                '&:active': { transform: 'translateY(0)' },
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                  color: 'text.disabled',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Register
            </LoadingButton>
          </Stack>
        )}
        {registrationDone && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 2 }}
          >
            <Link component={RouterLink} variant="subtitle2" to="/login">
              Back to login
            </Link>
          </Stack>
        )}
      </Form>
      {openOtpVerify && (
        <OTPVerification
          contactType={contactType}
          contact={values.contact}
          onClose={() => setOpenOtpVerify(false)}
          open={openOtpVerify}
          submitOtpFunc={submitOtp}
          handleSuccess={onSuccessfullVerify}
          countryCode={
            contactType === "phone" ? domainConfig.countryCode : ""
          }
          resendOtp={resendOtpFunc}
        />
      )}
    </FormikProvider>
  );
}
